/**
 * Claimant merge contract (#7064). Mirrors apps/pkg/domains/claimantmerge —
 * shape and unions live here so a backend change breaks the compile, not the UI.
 */

export type MergeBlockReason =
  | "pair_not_confirmed"
  | "slc_open_task"
  | "slc_frozen_bucket_occupied"
  | "slc_lookup_failed"
  | "snapshot_lookup_failed"
  | "laser_on_absorbed"
  | "contract_laser_on_absorbed"
  | "reported_authority_on_absorbed"
  | "contract_exclusion_on_absorbed"
  | "pooled_policy"
  | "family_deductible_claimant"
  | "pooled_lookup_failed"
  | "aura_synced"
  | "claimant_missing";

/**
 * Operator-facing copy per typed reason -- the ONLY text the UI renders for a
 * block. The backend `detail` strings are log/ledger-facing developer notes
 * (ticket refs, internal rule names) and are deliberately NOT shown to
 * operators (review !4800 thread 29168 / !4798 thread 29166).
 */
export const MERGE_BLOCK_LABELS: Record<MergeBlockReason, string> = {
  pair_not_confirmed: "The pair has not been confirmed",
  slc_open_task: "A stop-loss claim has an in-progress task",
  slc_frozen_bucket_occupied:
    "A claim funded through Aura is still marked 'new' where the other record also has an open claim — advance its status first",
  slc_lookup_failed: "Stop-loss claim state could not be verified",
  snapshot_lookup_failed: "Snapshot state could not be verified",
  laser_on_absorbed: "The absorbed record carries a laser the survivor lacks",
  contract_laser_on_absorbed: "The absorbed record carries a contract laser the survivor lacks",
  reported_authority_on_absorbed: "The absorbed record carries carrier-reported figures that cannot be rebuilt",
  contract_exclusion_on_absorbed: "The absorbed record is contract-excluded on a policy the survivor is not",
  family_deductible_claimant: "A record is marked family-deductible — merging is not available for it yet",
  pooled_lookup_failed: "Pooled-policy status could not be verified",
  pooled_policy: "Family-deductible (pooled) policies cannot be merged yet",
  aura_synced: "A record is synced to Aura",
  claimant_missing: "One of the records no longer exists",
};

export interface MergeBlockedEntry {
  reason: MergeBlockReason;
  entity_id: string;
  detail: string;
}

export type MergeSLCAction = "repoint" | "void_regenerate" | "blocked" | "frozen";

export interface MergeSLCDisposition {
  slc_id: string;
  /** Operator-facing claim number (SLC-...). */
  slc_aegle_id: string;
  claimant_id: string;
  policy_id?: string;
  claim_type: string;
  vendor_id?: string;
  claim_status: string;
  action: MergeSLCAction;
  reason?: MergeBlockReason;
  // shopspring decimal marshals UNQUOTED (see ClaimantMergePreview money note).
  total_paid_amount: number;
}

export interface MergeIdentifierResolution {
  space: "medical" | "rx";
  resolved: "parent" | "absorbed" | "none";
  absorbed_value_retained: boolean;
}

export interface MergeFieldConflict {
  field: string;
  parent_value: string;
  absorbed_value: string;
}

export interface MergeSurvivorSuggestion {
  claimant_id: string;
  reason: string;
}

/** Why a policy's post-merge amounts are not projected (#7244). */
export type MergeProjectionUnavailable =
  | "aggregate_specific_deductible"
  | "contract_laser"
  | "reported_authority"
  | "multiple_open_claims"
  | "lookup_failed"
  | "held_by_submitted_claim";

export const MERGE_PROJECTION_UNAVAILABLE_LABELS: Record<MergeProjectionUnavailable, string> = {
  aggregate_specific_deductible: "Aggregate specific deductible policy — amounts update when totals recompute",
  contract_laser: "Contract-lasered spend — amounts update when totals recompute",
  reported_authority: "Carrier-reported figures — amounts update when totals recompute",
  multiple_open_claims: "More than one open claim — amounts update when totals recompute",
  lookup_failed: "Amounts couldn't be projected — they update when totals recompute",
  held_by_submitted_claim:
    "A submitted claim still marked 'new' holds this policy's open claim — the remaining amount is held until it advances",
};

/**
 * Post-merge stop-loss outcome for one policy, computed server-side with the
 * engine's own netting rule (never re-derived here -- the SPA only renders it).
 * Money arrives as unquoted JSON numbers (shopspring decimal).
 */
export interface MergePolicyProjection {
  policy_id: string;
  /** Heading inputs (carrier + plan-year range, never aegle_id). Absent when the lookup failed. */
  carrier_vendor_id?: string;
  effective_start_date?: string;
  effective_end_date?: string;
  combined_paid: number;
  specific_deductible?: number;
  claimed_before: number;
  /** Absent when `unavailable` is set. */
  claimed_after?: number;
  /** What the single open claim carries after recompute (0 = none). */
  open_claim_after?: number;
  /** The existing claim that carries it; absent = recompute creates a new one. */
  open_claim_id?: string;
  unavailable?: MergeProjectionUnavailable;
}

export interface ClaimantMergePreview {
  pair_id: string;
  parent_id: string;
  absorbed_id: string;
  policy_id?: string;
  allowed: boolean;
  blocked: MergeBlockedEntry[] | null;
  slcs: MergeSLCDisposition[] | null;
  identifiers: MergeIdentifierResolution[] | null;
  conflicts: MergeFieldConflict[] | null;
  suggestion?: MergeSurvivorSuggestion;
  // Policies where BOTH records carry a live Claims Summary note: the merge
  // combines them into the survivor's note under an attribution line.
  // Informational, not blocking (reversal of the thread-29125 block, 2026-09-30).
  combined_note_policy_ids?: string[] | null;
  /** Per-policy post-merge stop-loss outcome (#7244). Empty on a blocked preview. */
  projections?: MergePolicyProjection[] | null;
  // shopspring/decimal marshals UNQUOTED, so these arrive as JSON numbers --
  // unlike the pair endpoints, whose DTO stringifies. Verified against the live
  // endpoint 2026-09-29; typed as number so nobody "fixes" a comparison by
  // string-comparing amounts.
  combined_paid: number;
  specific_deductible?: number;
  generated_at: string;
}

/** Conflict field keys -> the labels the review dialog already uses. */
export const MERGE_CONFLICT_FIELD_LABELS: Record<string, string> = {
  claimant_birth_date: "Date of birth",
  claimant_gender: "Gender",
  claimant_relationship: "Relationship",
  subscriber_ref: "Subscriber link",
};
